<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ApiResponsiveImage from '@/components/ApiResponsiveImage.vue';
import { type SiteLogo, isOptimisedSiteLogo } from '@/navTree';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';

const AUTOPLAY_INTERVAL_MS = 8000;

const props = defineProps<{
  partners: SiteLogo[];
}>();

const { t } = useI18n();

const scrollRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const isScrollable = computed(() => canScrollLeft.value || canScrollRight.value);

function updateScrollButtons(): void {
  const container = scrollRef.value;
  if (!container) return;
  const { scrollLeft, scrollWidth, clientWidth } = container;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 10;
}

function scroll(direction: 'left' | 'right'): void {
  const container = scrollRef.value;
  if (!container) return;
  const scrollAmount = 300;
  const next =
    direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;
  container.scrollTo({ left: next, behavior: 'smooth' });
  window.setTimeout(updateScrollButtons, 400);
}

let ro: ResizeObserver | null = null;
let autoplayTimer: number | null = null;

function tickAutoplay(): void {
  const container = scrollRef.value;
  if (!container) return;
  if (!isScrollable.value) return;
  if (!canScrollRight.value) {
    container.scrollTo({ left: 0, behavior: 'smooth' });
    window.setTimeout(updateScrollButtons, 400);
  } else {
    scroll('right');
  }
}

function startAutoplay(): void {
  stopAutoplay();
  if (typeof window === 'undefined') return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  if (!isScrollable.value) return;
  autoplayTimer = window.setInterval(tickAutoplay, AUTOPLAY_INTERVAL_MS);
}

function stopAutoplay(): void {
  if (autoplayTimer != null) {
    window.clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
}

function onUserNavClick(direction: 'left' | 'right'): void {
  scroll(direction);
  startAutoplay();
}

onMounted(() => {
  void nextTick(() => {
    updateScrollButtons();
    const el = scrollRef.value;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        updateScrollButtons();
        startAutoplay();
      });
      ro.observe(el);
    }
    startAutoplay();
  });
  window.addEventListener('resize', updateScrollButtons);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScrollButtons);
  ro?.disconnect();
  ro = null;
  stopAutoplay();
});

watch(
  () => props.partners.length,
  () => {
    void nextTick(() => {
      updateScrollButtons();
      startAutoplay();
    });
  }
);
</script>

<template>
  <div class="partners-carousel__breakout">
    <section
      class="partners-carousel"
      aria-labelledby="partners-carousel-heading"
    >
      <div class="partners-carousel__inner">
        <h2 id="partners-carousel-heading" class="partners-carousel__title">
          {{ t('partnersCarousel.title') }}
        </h2>

        <div
          class="partners-carousel__viewport"
          @mouseenter="stopAutoplay"
          @mouseleave="startAutoplay"
        >
          <button
            v-if="canScrollLeft"
            type="button"
            class="partners-carousel__nav partners-carousel__nav--left"
            :aria-label="t('partnersCarousel.scrollLeft')"
            @click="onUserNavClick('left')"
          >
            <svg
              class="partners-carousel__nav-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <button
            v-if="canScrollRight"
            type="button"
            class="partners-carousel__nav partners-carousel__nav--right"
            :aria-label="t('partnersCarousel.scrollRight')"
            @click="onUserNavClick('right')"
          >
            <svg
              class="partners-carousel__nav-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>

          <div
            ref="scrollRef"
            class="partners-carousel__scroll"
            @scroll="updateScrollButtons"
          >
            <div class="partners-carousel__track">
              <div
                v-for="(partner, index) in partners"
                :key="`${partner.id}-${index}`"
                class="partners-carousel__logo-wrap"
              >
                <ApiResponsiveImage
                  v-if="isOptimisedSiteLogo(partner)"
                  :image="partner"
                  :alt="partner.title || 'Partner logo'"
                  class="partners-carousel__logo partners-carousel__logo--optimised"
                  sizes="(max-width: 639px) 160px, 192px"
                  loading="lazy"
                />
                <img
                  v-else
                  class="partners-carousel__logo"
                  :src="partner.url"
                  :alt="partner.title || 'Partner logo'"
                  loading="lazy"
                  :width="partner.width"
                  :height="partner.height"
                  @error="onOfflineMediaImgError"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* Full-bleed below max-width column (match PageHeader featured breakout). */
.partners-carousel__breakout {
  margin-top: 2rem;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}
@media (min-width: 1024px) {
  .partners-carousel__breakout {
    margin-top: 3rem;
  }
}

.partners-carousel {
  width: 100%;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  /* py-8 sm:py-12 pb-[60px] */
  padding-top: 2rem;
  padding-bottom: 3.75rem;
}
@media (min-width: 640px) {
  .partners-carousel {
    padding-top: 3rem;
    padding-bottom: 3.75rem;
  }
}

.partners-carousel__inner {
  width: 100%;
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
  box-sizing: border-box;
}
@media (min-width: 640px) {
  .partners-carousel__inner {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .partners-carousel__inner {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

.partners-carousel__title {
  margin: 0;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 1.5rem;
}
@media (min-width: 640px) {
  .partners-carousel__title {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
}

.partners-carousel__viewport {
  position: relative;
}

.partners-carousel__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
  color: var(--color-text);
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease;
}
.partners-carousel__nav--left {
  left: 1rem;
}
.partners-carousel__nav--right {
  right: 1rem;
}
.partners-carousel__nav:hover:not(:disabled) {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-primary-foreground);
}
.partners-carousel__nav:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.partners-carousel__nav-icon {
  flex-shrink: 0;
}

.partners-carousel__scroll {
  overflow-x: auto;
  padding-left: 4rem;
  padding-right: 4rem;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.partners-carousel__scroll::-webkit-scrollbar {
  display: none;
}

.partners-carousel__track {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: max-content;
  margin-left: auto;
  margin-right: auto;
}
@media (min-width: 640px) {
  .partners-carousel__track {
    gap: 3rem;
  }
}
@media (min-width: 1024px) {
  .partners-carousel__track {
    gap: 4rem;
  }
}

.partners-carousel__logo-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 5rem;
  width: 10rem;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
  transition: opacity 0.3s ease;
}
@media (min-width: 640px) {
  .partners-carousel__logo-wrap {
    height: 6rem;
    width: 12rem;
  }
}

.partners-carousel__logo {
  flex-shrink: 0;
  align-self: center;
  margin-inline: auto;
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
}

/* Inner <img> lives in ApiResponsiveImage; scoped CSS requires :deep() to override defaults. */
.partners-carousel__logo--optimised.api-responsive-image,
picture.api-responsive-image.partners-carousel__logo--optimised {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 100%;
  max-width: 100%;
  width: 100%;
  height: 100%;
  margin: 0;
  box-sizing: border-box;
}

.partners-carousel__logo--optimised.api-responsive-image :deep(.api-responsive-image__img) {
  flex-shrink: 0;
  align-self: center;
  margin-inline: auto;
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  margin-block: 0;
  display: block;
  object-fit: contain;
  object-position: center;
}
</style>
